import { create, type JsonValue, type MessageShape } from "@bufbuild/protobuf";
import { ValueSchema } from "@bufbuild/protobuf/wkt";
import { LRUCache } from "lru-cache";
import stringify from "safe-stable-stringify";
import { ERR_INVALID_NUMBER } from "#lib/validation.js";

const NUMBER_PATTERN = /^-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;

export function numberFromStringValue(value: string): number | undefined {
	const trimmed = value.trim();
	if (trimmed === "") {
		return undefined;
	}
	if (!NUMBER_PATTERN.test(trimmed)) {
		return undefined;
	}
	const n = Number(trimmed);
	if (!Number.isFinite(n)) {
		return undefined;
	}
	return n;
}

export function validateNumber(value: string | number | undefined): string | undefined {
	if (value === undefined) {
		return undefined;
	}
	if (typeof value === "number") {
		return Number.isFinite(value) ? undefined : ERR_INVALID_NUMBER;
	}
	const trimmed = value.trim();
	if (trimmed === "") {
		return undefined;
	}
	return numberFromStringValue(trimmed) === undefined ? ERR_INVALID_NUMBER : undefined;
}

export function formattedNumber(value?: JsonValue): string {
	if (typeof value === "number") {
		return String(value);
	}
	if (typeof value === "string") {
		return value;
	}
	return "";
}

function makeNumberValue(val: number): MessageShape<typeof ValueSchema> {
	return create(ValueSchema, { kind: { case: "numberValue", value: val } });
}

export function makeNumberValueFromString(val: string): MessageShape<typeof ValueSchema> | undefined {
	const n = numberFromStringValue(val);
	return n === undefined ? undefined : makeNumberValue(n);
}

const numberFormatters: LRUCache<string, Intl.NumberFormat> = new LRUCache({ max: 10, ttlAutopurge: true });

const DEFAULT_NUMBER_FORMAT_OPTIONS: Intl.NumberFormatOptions = {
	notation: "compact",
	maximumFractionDigits: 2,
};
const DEFAULT_INTEGER_FORMAT_OPTIONS: Intl.NumberFormatOptions = {
	notation: "compact",
	maximumFractionDigits: 0,
};

export function formatNumber(num: number | null | undefined, options?: Intl.NumberFormatOptions): string {
	if (num === null || num === undefined) {
		return "";
	}
	const formattingOptions: Intl.NumberFormatOptions = options ?? DEFAULT_NUMBER_FORMAT_OPTIONS;
	const key = stringify(formattingOptions);
	let formatter = numberFormatters.get(key);
	if (!formatter) {
		formatter = new Intl.NumberFormat("en-US", formattingOptions);
		numberFormatters.set(key, formatter);
	}
	return formatter.format(num);
}

export function formatPercent(pct: number, options?: Intl.NumberFormatOptions): string {
	const formattingOptions: Intl.NumberFormatOptions = {
		notation: "compact",
		maximumFractionDigits: 2,
		...options,
		style: "percent",
	};
	const key = stringify(formattingOptions);
	let formatter = numberFormatters.get(key);
	if (!formatter) {
		formatter = new Intl.NumberFormat("en-US", formattingOptions);
		numberFormatters.set(key, formatter);
	}
	if (!options) {
		let rounded = Math.round(pct);
		if (rounded == 0 && pct != 0) {
			rounded = 1;
			return "<" + formatter.format(rounded / 100);
		}
		return formatter.format(rounded / 100);
	}
	return formatter.format(pct / 100);
}

export function formatInteger(num?: number | null, options?: Intl.NumberFormatOptions): string {
	if (num === null || num === undefined) return "";
	const formattingOptions: Intl.NumberFormatOptions = options ?? DEFAULT_INTEGER_FORMAT_OPTIONS;
	const key = stringify(formattingOptions);
	let formatter = numberFormatters.get(key);
	if (!formatter) {
		formatter = new Intl.NumberFormat("en-US", formattingOptions);
		numberFormatters.set(key, formatter);
	}
	return formatter.format(num);
}
